import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { IntervalService } from 'src/app/device-command/services/interval.service';
import { DeviceMonitoringModalLinkComponent } from 'src/app/monitoring/device-monitoring-modal-link/device-monitoring-modal-link.component';
import {
  DeviceShadowService,
  IDeviceShadowRecord,
  IShadowQueryResponse,
  ShadowQueryDateProperty,
  ShadowQueryRequest,
  ShadowQueryValueProperty,
  ShadowQueryValueRangeProperty,
} from '../../shared/services/device-shadow/device-shadow.service';

// This service is responsible for manipulating device shadow data to fit the table in the insights page
@Injectable({
  providedIn: 'root',
})
export class ShadowPageService {
  defaultColumnConfiguration: ColDef = {
    sortable: false,
    resizable: true,
  };

  totalRecordCount: number;
  pageCount: number;

  isPolling = false;
  requestIds: Map<string, CsvDetails> = new Map();

  columnDefinitions: ColDef[] = [
    {
      field: ShadowQueryValueProperty.serialNumber,
      headerName: 'DriveCam Device',
      headerTooltip: 'Lytx Event Recorder Serial Number',
      cellRenderer: DeviceMonitoringModalLinkComponent,
    },
    {
      field: ShadowQueryValueProperty.companyName,
      headerName: 'Company',
      headerTooltip: 'Company Name',
    },
    {
      field: ShadowQueryValueProperty.companyId,
      headerName: 'ID',
      headerTooltip: 'Company ID',
    },
    {
      field: ShadowQueryValueProperty.groupName,
      headerName: 'Group',
      headerTooltip: 'Group Name',
    },
    {
      field: ShadowQueryValueProperty.geotabSerialNumber,
      headerName: 'Telematics Device',
      headerTooltip: 'Telematics Device',
    },
    {
      ...this.createBooleanColumn(
        ShadowQueryValueProperty.geotabConnectionState,
        'Telematics Connection',
        'Telematics Connection',
        ['Connected', 'Disconnected'],
      )
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.geotabLastCommunicated,
        'Telematics Last Communicated',
        'Telematics Last Communicated',
      )
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.lastPairedMessage,
        'Last Telematics Pairing Message',
        'Last Telematics Pairing Message',
      ),
    },
    {
      field: ShadowQueryValueProperty.vehicleName,
      headerName: 'Vehicle Name',
      headerTooltip: 'Company Assigned Vehicle Name',
    },
    {
      field: ShadowQueryValueProperty.vehicleId,
      headerName: 'Vehicle ID',
      headerTooltip: 'Vehicle ID',
    },
    {
      field: ShadowQueryValueProperty.operationalState,
      headerName: 'Operating state',
      headerTooltip: 'Operating state',
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.installationDate,
        'Install Date',
        'Date that installation started',
      ),
    },
    {
      field: ShadowQueryValueProperty.diliRecordNumber,
      headerName: 'DILI Record',
      headerTooltip: 'Daily Installation Line Item',
    },
    {
      field: ShadowQueryValueProperty.calPickStatus,
      headerName: 'Calibration Status',
      headerTooltip: 'Calibration Status',
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.factoryInstallDate,
        'Factory Initialization Date',
        'Date of factory install and configuration (SF500 only)',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.initialDockDate,
        'Initial Dock Date',
        'Initial Device Check-In Date',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.lastCommunicationDate,
        'Last Communication Date',
        'Last Date Device Communicated with our Backend',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.checkIn,
        'Last Dock Date',
        'Last Device Check-In Date',
      ),
    },
    {
      ...this.createShadowTimestampColumn(
        ShadowQueryDateProperty.trackPoint,
        'GPS Points',
        'Last date that GPS points were captured',
      ),
    },
    {
      ...this.createShadowTimestampColumn(
        'installTrackPoint',
        'Install GPS Points',
        'Last date that GPS points were captured during installation',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.ignitionOn,
        'Ignition On',
        'Last date that Ignition On signal was captured',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.ignitionOff,
        'Ignition Off',
        'Last date that Ignition Off signal was captured',
      ),
    },
    {
      field: ShadowQueryValueProperty.can1Type,
      headerName: 'Vehicle CAN Type',
      headerTooltip:
        'Type of  Controller Area Network (CAN) programmed for this vehicle',
    },
    {
      ...this.createShadowValueColumn(
        ShadowQueryDateProperty.ecmVin,
        'VIN (ECM)',
        'Vehicle Identification Number provided by the CAN interface',
      ),
    },
    {
      ...this.createShadowTimestampColumn(
        ShadowQueryDateProperty.ecmOdometer,
        'ECM Odometer',
        'Last vehicle Odometer data point provided by the CAN interface',
      ),
    },
    {
      ...this.createShadowTimestampColumn(
        ShadowQueryDateProperty.ecmEngineSpeed,
        'ECM Engine Speed',
        'Last vehicle Engine Speed (RPM) data point provided by the CAN interface',
      ),
    },
    {
      ...this.createShadowTimestampColumn(
        ShadowQueryDateProperty.ecmSpeed,
        'ECM Vehicle Speed',
        'Last vehicle Vehicle Speed data point provided by the CAN interface',
      ),
    },
    {
      field: ShadowQueryValueProperty.heading,
      headerName: 'ECM Bearing',
      headerTooltip: 'Last GPS heading (compass direction) reported by the device',
    },
    {
      field: ShadowQueryValueProperty.cableType,
      headerName: 'Cable Type Selected',
      headerTooltip:
        'CAN interface Cable Type selected/installed using Lytx Installation Application (LIA)',
    },
    {
      field: ShadowQueryValueProperty.ignitionDetectMethod,
      headerName: 'Ignition Detect Method',
      headerTooltip: 'Method used to detect vehicle ignition',
    },
    {
      field: ShadowQueryValueProperty.motionBasedIgnitionEnabled,
      headerName: 'Enable Motion Based Ignition',
      headerTooltip:
        'Setting which determines vehicle ignition using accelerometer (motion) only',
    },
    {
      field: ShadowQueryValueProperty.j1939VirtualIgnitionEnabled,
      headerName: 'Enable J1939 Virtual Ignition',
      headerTooltip:
        'Setting which determines vehicle ignition using ECM data (RPM or Speed)',
    },
    {
      field: ShadowQueryValueProperty.vnetStreamEnabled,
      headerName: 'Enable VNET Stream - MQTT',
      headerTooltip:
        'Setting which enables ECM data to be transmitted via MQTT',
    },
    {
      field: ShadowQueryValueProperty.continuousDataEnabled,
      headerName: 'Enable Continuous Data',
      headerTooltip: 'Setting which enables Legacy Telemetric data',
    },
    {
      field: ShadowQueryValueProperty.obdiiProvider,
      headerName: 'OBDII Provider',
      headerTooltip: 'OBD provider used by this device (e.g., Xirgo, OBD Experts)',
    },
    {
      field: ShadowQueryValueProperty.obdiiLookupProvider,
      headerName: 'OBDII Lookup Provider',
      headerTooltip: 'Lookup service used to decode proprietary PIDs and VIN data',
    },
    {
      field: ShadowQueryValueProperty.enableObd2ReqsOnlyIfVehLikelyOn,
      headerName: 'Enable OBDII Requests Only If Vehicle is Likely ON',
      headerTooltip: 'Setting which enables OBDII requests only when the vehicle is likely on',
    },
    {
      field: ShadowQueryValueProperty.vin,
      headerName: 'VIN (Salesforce)',
      headerTooltip:
        'Vehicle Identification Number provided by SalesForce database',
    },
    {
      field: ShadowQueryValueProperty.vehicleYear,
      headerName: 'Vehicle Year',
      headerTooltip:
        'Vehicle Year (Priority ECM else Salesforce. If blank no ECM and no SalesForce)',
    },
    {
      field: ShadowQueryValueProperty.vehicleMake,
      headerName: 'Vehicle Make',
      headerTooltip:
        'Vehicle Make (Priority ECM else Salesforce. If blank no ECM and no SalesForce)',
    },
    {
      field: ShadowQueryValueProperty.vehicleModel,
      headerName: 'Vehicle Model',
      headerTooltip:
        'Vehicle Model (Priority ECM else Salesforce. If blank no ECM and no SalesForce)',
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.sdCardFailure,
        'SD Card Failure',
        'SD Card Failure',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.sdCardSuccess,
        'SD Card Success',
        'SD Card Success',
      ),
    },
    {
      ...this.createDateColumn(
        'installSdCardSuccess',
        'Install SD Card Success',
        'Install SD Card Success',
      ),
    },
    {
      field: ShadowQueryValueProperty.platform,
      headerName: 'Hardware Platform',
      headerTooltip: 'Event Recorder Hardware Platform',
    },
    {
      field: ShadowQueryValueProperty.hardwareVersion,
      headerName: 'Hardware Version',
      headerTooltip: 'Event Recorder Hardware Version',
    },
    {
      ...this.createShadowValueColumn(
        ShadowQueryValueProperty.firmwareVersion,
        'Firmware Version',
        'Event Recorder Firmware Version',
      ),
    },
    {
      field: ShadowQueryValueProperty.intendedFirmwareVersion,
      headerName: 'Pending Firmware Version',
      headerTooltip:
        'Firmware Version to which the device is supposed to update',
    },
    {
      field: ShadowQueryValueProperty.firmwareVersionAuthoritativeEntity,
      headerName: 'Firmware Version Origin',
      headerTooltip:
        'Entity type and id from which the firmware version setting is taken',
      cellRenderer: (params: any): string => {
        return this.getFirmwareVersionAuthoritativeEntity(
          params.data.firmwareVersionAuthoritativeEntity,
        );
      },
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.presenceOn,
        'Presence On',
        'Last date that the Event Recorder was turned on',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.presenceOff,
        'Presence Off',
        'Last date that the Event Recorder was turned off',
      ),
    },
    {
      ...this.createShadowValueColumn(
        ShadowQueryValueRangeProperty.upTime,
        'Uptime',
        'Time that Event Recorder is not in Hibernation',
      ),
    },
    {
      field: ShadowQueryValueRangeProperty.hibernationDelayMinutes,
      headerName: 'Hibernation Delay',
      headerTooltip: 'Minutes the device waits to hibernate after ignition off',
    },
    {
      field: ShadowQueryValueProperty.statusId,
      headerName: 'Status ID',
      headerTooltip: 'Event Recorder Status ID',
    },
    {
      field: ShadowQueryValueProperty.followerSerial,
      headerName: 'Follower ER Serial Number',
      headerTooltip: 'Follower ER Serial Number',
    },
    {
      field: ShadowQueryValueProperty.followerState,
      headerName: 'Follower ER State',
      headerTooltip: 'Follower ER State',
    },
    {
      field: ShadowQueryValueProperty.vehicleClass,
      headerName: 'Vehicle Class',
      headerTooltip: 'Vehicle Class',
    },
    {
      field: ShadowQueryValueRangeProperty.vehicleWidth,
      headerName: 'Vehicle Width',
      headerTooltip: 'Vehicle Width',
    },
    {
      field: ShadowQueryValueRangeProperty.cameraHeight,
      headerName: 'Camera Height',
      headerTooltip: 'Camera Height',
    },
    {
      field: ShadowQueryValueRangeProperty.cameraOffset,
      headerName: 'Camera Offset',
      headerTooltip: 'Camera Offset',
    },
    {
      field: ShadowQueryValueProperty.installerType,
      headerName: 'Installer Type',
      headerTooltip: 'Installer Type',
    },
    {
      field: ShadowQueryValueProperty.vehicleCalibration,
      headerName: 'Vehicle Calibration',
      headerTooltip: 'Vehicle Calibration',
    },
    {
      ...this.createShadowValueColumn(
        ShadowQueryValueRangeProperty.systemVoltage,
        'System Voltage',
        'System Voltage',
      ),
    },
    {
      ...this.createShadowValueColumn(
        ShadowQueryValueRangeProperty.batteryVoltage,
        'Battery Voltage',
        'Battery Voltage',
      ),
    },
    {
      ...this.createShadowValueColumn(
        'installBatteryVoltage',
        'Install Battery Voltage',
        'Install Battery Voltage',
      ),
    },

    {
      ...this.createShadowValueColumn(
        ShadowQueryValueRangeProperty.remainingWakeOnModemTime,
        'Remaining Wake On Modem Time',
        'Time left before the Event Recorder goes into Hibernation',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.lastAccelerometerDateTime,
        'Last Accelerometer Date',
        'Last Accelerometer Date',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.lastTripDate,
        'Last Trip Date',
        'Last Trip Date',
      ),
    },
    {
      ...this.createShadowTimestampColumn(
        ShadowQueryDateProperty.ecmEngineHours,
        'ECM Engine Hours',
        'Last vehicle Engine Hours data point provided by the CAN interface',
      ),
    },
    {
      ...this.createShadowValueColumn(
        ShadowQueryValueProperty.deviceDetectedBusType,
        'Device Detected Bus Type',
        'Detected Bus Type for ER',
      ),
    },
    {
      ...this.createDateColumn(
        ShadowQueryDateProperty.motionIntervalStart,
        'Motion Interval Start',
        'Start date of the most recent motion interval',
      ),
    },
    {
      field: ShadowQueryValueRangeProperty.motionWithoutIgnitionSpan,
      headerName: 'Motion Without Ignition Span',
      headerTooltip:
        'Span in days between the last ignition and the start of the last motion interval',
    },
    {
      field: ShadowQueryValueRangeProperty.shippedCable,
      headerName: 'Shipped Cable',
      headerTooltip: 'Salesforce Cable Shipped Info',
    },
    {
      field: ShadowQueryValueRangeProperty.salesforceVehicleId,
      headerName: 'Salesforce Vehicle Id',
      headerTooltip: 'The salesforce vehicle ID.',
    },
    {
      field: ShadowQueryValueRangeProperty.obdiiAccessories,
      headerName: 'OBDII Accessories',
      headerTooltip: 'Salesforce OBDII Vehicle Accessories',
    },
    {
      field: ShadowQueryValueRangeProperty.hdVehicleAccessory,
      headerName: 'HD Vehicle Accessory',
      headerTooltip: 'Salesforce HD Vehicle Accessories',
    },
    {
      field: ShadowQueryValueProperty.installStreamsExpected,
      headerName: 'Install Streams Expected',
      headerTooltip: 'ECM streams expected during install',
    },
    {
      field: ShadowQueryValueProperty.installStreamsFound,
      headerName: 'Install Streams Found',
      headerTooltip: 'ECM streams found during install',
    },
  ];

  constructor(
    private deviceShadowService: DeviceShadowService,
    private intervalService: IntervalService,
    private toastr: ToastrService,
  ) { }

  async getData(request?: ShadowQueryRequest): Promise<IShadowQueryResponse> {
    try {
      const response = await this.deviceShadowService.getDeviceShadows(request);
      const geotabSerialNumbers = response.results
        .map((item: any) => item.geotabSerialNumber)
        .filter((sn: string) => !!sn);

      if (geotabSerialNumbers.length > 0) {
        const geotabRequest: ShadowQueryRequest = {

          deviceType: 'GEOTAB',
        };

        const geotabResponse = await this.deviceShadowService.getDeviceShadows(geotabRequest);

        response.results.forEach((item: any) => {
          const geotabDetail = geotabResponse.results.find((g: any) => g.serialNumber === item.geotabSerialNumber);
          if (geotabDetail) {
            item.lastPairedMessage = geotabDetail.lastPairedMessage;
            item.geotabLastCommunicated = geotabDetail.lastCommunicationDate;
            item.geotabConnectionState = this.getTelematicsConnectionState(geotabDetail);
          }
        });
      }

      if (response.results.length < request.count) {
        this.toastr.info('End of Results');
        if (response.scrollId) {
          await this.deviceShadowService.deleteScrollId(response.scrollId);
        }
        response.scrollId = null;
      }

      return response;
    } catch (error) {
      this.toastr.error('Failure retrieving data.');
      return null;
    }
  }

  getTelematicsConnectionState(response: IDeviceShadowRecord): boolean {
    const result = response.connectionState &&
      (!response.lastCommunicationDate || (new Date().getTime() - new Date(response.lastCommunicationDate).getTime()) <= 24 * 60 * 60 * 1000)
      ? true
      : false;
    return result;
  }

  async getCsv(request?: ShadowQueryRequest): Promise<void> {
    const requestId = await this.deviceShadowService.requestCsv({
      ...request,
      scrollId: null,
    });
    this.requestIds.set(requestId, { name: request.name, secondsPassed: 0 });
    this.toastr.info('Generating CSV. You will be notified when complete.');
    if (!this.isPolling) {
      this.startPolling();
    }
  }

  deleteScrollId(scrollId: string): Promise<void> {
    return this.deviceShadowService.deleteScrollId(scrollId);
  }

  private async getCsvUrl() {
    for (const requestId of Array.from(this.requestIds.keys())) {
      this.updatePassedSeconds(requestId);

      const response = await this.deviceShadowService.getCsvUrl(requestId);
      const status = response.status;
      const url = response.url;

      if (this.hasOneMinutePassed(requestId)) {
        this.toastr.info(
          `CSV for query ${this.requestIds.get(requestId).name.toUpperCase()} is still being generated. You will be notified with it completes`,
        );
      }

      if (status === 'Failed') {
        this.toastr.error('CSV download failed. Please try again');
        this.requestIds.delete(requestId);
      }

      if (status == 'Complete') {
        this.downloadCsv(url, this.requestIds.get(requestId).name);
        this.requestIds.delete(requestId);
      }
    }

    if (this.requestIds.size == 0) {
      this.stopPolling();
    }
  }

  private updatePassedSeconds(requestId: string) {
    this.requestIds.get(requestId).secondsPassed += 10;
  }

  private hasOneMinutePassed(requestId: string) {
    return this.requestIds.get(requestId).secondsPassed % 60 == 0;
  }

  private downloadCsv(url: string, queryName: string) {
    this.toastr.info('CSV is downloading now.');

    const tempElemement = document.createElement('a');
    tempElemement.href = url;
    const filename = `${formatDate(new Date(), 'short', 'en-US')}_${queryName}_Lytx.csv`;
    tempElemement.setAttribute('download', filename);
    document.body.appendChild(tempElemement);
    tempElemement.click();
    document.body.removeChild(tempElemement);
  }

  private startPolling() {
    this.isPolling = true;
    this.intervalService.setInterval(async () => await this.getCsvUrl(), 10000);
  }

  private stopPolling() {
    this.intervalService.clearInterval();
    this.isPolling = false;
  }

  private createDateColumn(
    field: string,
    headerName: string,
    headerTooltip: string,
  ): ColDef {
    return {
      field: field,
      valueGetter: (params: any): string => {
        const dateString = params.data[field];
        try {
          return dateString ? formatDate(dateString, 'short', 'en-US') : '';
        } catch (error) {
          console.error(error, dateString);
          return 'INVALID FORMAT';
        }
      },
      headerName: headerName,
      headerTooltip: headerTooltip,
    };
  }

  private createBooleanColumn(
    field: string,
    headerName: string,
    headerTooltip: string,
    values: [string, string],
  ): ColDef {
    return {
      field: field,
      valueGetter: (params: any): string => {
        const value = params.data[field];
        return value ? values[0] : values[1];
      },
      headerName: headerName,
      headerTooltip: headerTooltip,
    };
  }

  private createShadowTimestampColumn(
    field: string,
    headerName: string,
    headerTooltip: string,
  ): ColDef {
    return {
      field: field,
      valueGetter: (params: any): string => {
        const value = params.data[field];
        if (value && value.timestamp) {
          try {
            return formatDate(value.timestamp, 'short', 'en-US');
          } catch (error) {
            console.error(error, value.timestamp);
            return 'INVALID FORMAT';
          }
        }
        return '';
      },
      headerName: headerName,
      headerTooltip: headerTooltip,
    };
  }

  private createShadowValueColumn(
    field: string,
    headerName: string,
    headerTooltip: string,
  ): ColDef {
    return {
      field: field,
      valueGetter: (params: any): string => {
        const value = params.data[field];
        return value ? value.value : '';
      },
      headerName: headerName,
      headerTooltip: headerTooltip,
    };
  }

  private getFirmwareVersionAuthoritativeEntity(
    firmwareVersionAuthoritativeEntity: string,
  ): string {
    if (!firmwareVersionAuthoritativeEntity) {
      return '';
    }

    const entityType = this.getEntityType(firmwareVersionAuthoritativeEntity);
    const entityId = this.getEntityId(firmwareVersionAuthoritativeEntity);

    if (
      entityType === 'Root' ||
      (entityType === 'Group' &&
        entityId === '00000000-0000-0000-0000-000000000000')
    ) {
      return 'Root';
    }

    return firmwareVersionAuthoritativeEntity;
  }

  private getEntityType(entity: string): string {
    return entity.split('#')[0];
  }

  private getEntityId(entity: string): string {
    return entity.split('#')[1];
  }
}

interface CsvDetails {
  name: string;
  secondsPassed: number;
}
